import { useMemo } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_FORMS_COLUMNS } from '@mobileConstants'
import { useMobileForms, useConfigPage, useAllDrafts } from '@mobileHooks'
import { MobileForm } from '@mobileTypes'
import { Box, Button, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileForms = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllForms = matchPath(ROUTES.CONFIG_MOBILE_FORMS, pathname)

  const { forms, loading, copyForm, deleteForm } = useMobileForms()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileForm>('mobile-form-draft-')

  const {
    handleCreate,
    handleEdit,
    handleCopyClick,
    handleExport,
    handleDeleteClick,
    copyDialogProps,
    deleteDialogProps,
  } = useConfigPage({
    items: forms,
    copyItem: copyForm,
    deleteItem: deleteForm,
    createRoute: ROUTES.CONFIG_MOBILE_FORMS_CREATE,
    editRoute: id => `${ROUTES.CONFIG_MOBILE_FORMS_EDIT}/${id}`,
    exportFileName: form => `${form.name}_form_config.json`,
    copyDialogTitle: 'Copy Mobile Form',
  })

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e]),
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = forms
      .filter(f => draftEntityIdMap.has(String(f.id)))
      .map(f => {
        const entry = draftEntityIdMap.get(String(f.id))!
        return { ...f, ...entry.draft.data, id: f.id, _isDraft: true, _isNewDraft: false, _draftKey: entry.key }
      })

    const serverRows = forms.filter(f => !draftEntityIdMap.has(String(f.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [forms, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_FORMS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_FORMS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_FORMS_EDIT}/${params.row.id}`, { state: { restoreDraft: true } })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.id)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.id)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        {...copyDialogProps}
        initialInternalId={copyDialogProps.initialInternalId}
        existingInternalIds={copyDialogProps.existingInternalIds}
        onCopy={copyDialogProps.onConfirm}
      />

      <DeleteConfirmDialog
        open={deleteDialogProps.open}
        title='Delete Mobile Form'
        message='Are you sure you want to delete form'
        itemName={deleteDialogProps.itemToDelete?.name}
        onClose={deleteDialogProps.onClose}
        onConfirm={deleteDialogProps.onConfirm}
      />

      <Box sx={{ ...(!isAllForms && { display: 'none' }), flex: 1 }}>
        <PageContentLayout sx={{ p: 1 }}>
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                Mobile Forms
              </Typography>
            }
          />

          <Box sx={{ mb: 2 }}>
            <Button variant='contained' onClick={handleCreate}>
              Create Mobile Form
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid rows={rows} columns={columns} loading={loading} disableRowSelectionOnClick hideFooter />
          </Box>
        </PageContentLayout>
      </Box>
    </>
  )
}
